<script setup>
import upperFirst from 'lodash/upperFirst'
import { computed, onBeforeMount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import useUser from '../../../Composables/User/useUser.ts'
import BlockContent from '../Components/UI/Content/BlockContent.vue'
import CardContent from '../Components/UI/Content/CardContent.vue'
import MainContainer from '../Components/UI/Content/MainContainer.vue'
import PageLayout from '../Components/UI/PageLayout.vue'
import DataTable from '../Components/UI/Table/DataTable.vue'
import TableMenu from '../Components/UI/Table/TableMenu.vue'
import useApi from '../Composables/useApi'
import useContractExport from '../Composables/useContractExport'
import usePlatform from '../Composables/usePlatform'
import useSearchParams from '../Composables/useSearchParams'

const { t } = useI18n()
const { getJSON } = useApi()
const { isPlatformClient, isClient, isPlatform } = usePlatform()
const { user } = useUser()
const { searchParams } = useSearchParams()
const { exportContracts, canDownloadExport } = useContractExport()

const contracts = ref([])
const contractKeys = ref([])
const isLoaded = ref(false)
const selectedStatus = ref(parseInt(searchParams.get('s') || 0))
const contractList = ref(null)
const contractListRequest = ref(null)

const filters = ref({
  units: [],
  platformSalesmen: [],
})

const filtersValues = ref({
  unit: null,
  platformSalesman: null,
})

const submenuLabels = computed(() => [
  { id: 0, name: t('contract.list.type.status.ALL') },
  { id: 1, name: t('contract.list.type.status.CREATED') },
  { id: 2, name: t('contract.list.type.status.PENDING') },
  { id: 3, name: t('contract.list.type.status.CLIENT_SIGNED') },
  { id: 4, name: t('contract.list.type.status.OTHER') },
])

const tableFilters = computed(() => {
  return [
    { name: 'unit', optionsKey: 'units', domain: 'simulation', labelTop: true, condition: true },
    {
      name: 'platformSalesman',
      optionsKey: 'platformSalesmen',
      domain: 'simulation',
      labelTop: true,
      condition: !user.value?.isPlatformClient,
    },
  ].filter(({ condition }) => condition === true)
})

const getDynamicColumns = (opts = {}) => {
  const { hasLink = true, isPlatformClientVal = false } = opts
  return contractKeys.value.map((key) => {
    const uppedKey = upperFirst(key)

    switch (key) {
      case 'id':
        return {
          data: key,
          displayable: false,
        }
      case 'number': {
        const col = {
          name: key,
          title: t(`contract.list.table.label${uppedKey}`),
        }
        if (hasLink) {
          return {
            ...col,
            formatter: 'context',
            options: { renderType: 'contract', renderId: 'id' },
          }
        }
        return col
      }
      case 'status':
        return {
          name: key,
          title: t(`contract.list.table.label${uppedKey}`),
          formatter: 'status',
          options: { renderType: 'contract' },
        }
      case 'orderNumber': {
        const path = isPlatformClientVal ? 'platformClient.' : ''
        return {
          name: key,
          title: t(`contract.list.table.${path}label${uppedKey}`),
        }
      }
      case 'platformSalesman':
        return {
          name: key,
          title: t(`contract.list.table.label${uppedKey}`),
        }
      case 'rentAmount':
      case 'totalAmount':
        return {
          name: key,
          title: t(`contract.list.table.label${uppedKey}`),
          type: 'num-fmt',
          formatter: 'currency',
        }
      case 'createdAt':
      case 'paidAt':
        return {
          name: key,
          title: t(`contract.list.table.label${uppedKey}`),
          type: 'date',
          formatter: 'date',
        }
      default:
        return {
          name: key,
          title: t(`contract.list.table.${key}`),
        }
    }
  })
}

const url = computed(() => {
  searchParams.set('module', 'contract')
  searchParams.set('s', selectedStatus.value)
  return 'contracts/client?' + searchParams.toString()
})

const columns = computed(() => {
  return contractKeys.value.length > 0
    ? getDynamicColumns({ hasLink: false, isPlatformClientVal: isPlatformClient.value })
    : [{ data: 'id', displayable: false }]
})

const showDownloadExportButton = computed(() => {
  return canDownloadExport(user.value?.roles || []) && isPlatformClient.value
})

const cancelPendingRequest = () => {
  contractListRequest.value?.cancel()
  contractListRequest.value = null
}

const fetchContracts = async () => {
  cancelPendingRequest()
  const getJsonPromise = getJSON(url.value)
  contractListRequest.value = getJsonPromise
  contracts.value = await getJsonPromise
  if (contractKeys.value.length === 0 && contracts.value.length > 0) {
    contractKeys.value = Object.keys(contracts.value[0])
  }
  isLoaded.value = true
  contractList.value?.renderDataTable()
}

const getContracts = async (callback) => {
  cancelPendingRequest()

  callback({ data: contracts.value })
}

const updateFilterData = (newFilters) => {
  for (const [name, value] of Object.entries(newFilters)) {
    const newValue = typeof value === 'string' || value > 0 ? value : null

    if (newValue) {
      searchParams.set(name, newValue)
    } else {
      searchParams.delete(name)
    }
  }
  fetchContracts()
}

const setSelectedStatus = (value) => {
  value = parseInt(value)
  if (value !== selectedStatus.value) {
    selectedStatus.value = value
    updateParams()
  }
}

const updateParams = () => {
  let statuses = []
  switch (selectedStatus.value) {
    case 1:
      statuses.push('CREATED')
      break
    case 2:
      statuses.push('VALIDATED', 'STUDY', 'STUDIED', 'PENDING')
      break
    case 3:
      statuses.push('CLIENT_SIGNED', 'CHECKED', 'ORDERED', 'WAITING', 'COMPANY_SIGNED')
      break
    case 4:
      statuses.push('DELIVERED', 'COMPLETE')
      break
    default:
      statuses.push(
        'CREATED',
        'VALIDATED',
        'STUDY',
        'STUDIED',
        'PENDING',
        'CLIENT_SIGNED',
        'CHECKED',
        'ORDERED',
        'WAITING',
        'COMPANY_SIGNED',
        'DELIVERED',
        'COMPLETE',
      )
  }
  searchParams.set('s', selectedStatus.value)
  searchParams.set('status', statuses.join('-'))
  fetchContracts()
}

const download = async () => {
  await exportContracts('contracts/export/client', searchParams)
}

onBeforeMount(() => {
  if (!(isClient.value || isPlatform.value)) {
    window.location = '/404.php'
  }
  if (!isPlatformClient.value) {
    window.location = '/portefeuille/client.php'
  }
  updateParams()
})
</script>
<template>
  <PageLayout menu="contracts" client>
    <MainContainer title-t="menu.myContracts">
      <BlockContent>
        <TableMenu
          :status="selectedStatus"
          :filters="tableFilters"
          :filter-values="filtersValues"
          :filter-options="filters"
          :submenu-labels="submenuLabels"
          :show-filters-button="isPlatformClient"
          :show-download-button="showDownloadExportButton"
          endpoint="contract"
          @set-selected-status="setSelectedStatus"
          @update-filter-data="updateFilterData"
          @download="download"
        />
        <CardContent>
          <DataTable
            v-if="isLoaded"
            id="contractList"
            ref="contractList"
            :columns="columns"
            :fetch="getContracts"
            :order="contractKeys.length >= 2 ? [[2, 'desc']] : []"
            :zero-records="t('contract.list.zeroRecords')"
          />
        </CardContent>
      </BlockContent>
    </MainContainer>
  </PageLayout>
</template>
